import { useAuth } from '@/context/AuthContext';
import { Link, PAGE_PATHS, useRouter } from '@/lib/router';
import { PAYMENT_MARKS, SOCIAL_LINKS } from './footerData';

const LEGAL_LINKS = [
  { label: 'Privacy Policy', href: PAGE_PATHS.privacy },
  { label: 'Terms of Service', href: PAGE_PATHS.terms },
  { label: 'Refund Policy', href: PAGE_PATHS.refunds },
  { label: 'Shipping Policy', href: PAGE_PATHS.shipping },
  { label: 'Health Disclaimer', href: PAGE_PATHS.disclaimer },
] as const;

/**
 * The site footer (bundle `fs()`, ui-layer.md §2.8). UI-R6: every navigation item is a real
 * anchor with production's classes (the bundle used `span[role=button]`). The glyph spans stay
 * in the markup; the stylesheet hides them.
 */
export function Footer() {
  const { user } = useAuth();
  const { navigate } = useRouter();

  return (
    <footer className="footer">
      <div className="footer__content">
        <div className="footer__brand">
          <img
            src="./assets/logos/logo-full-red.png"
            alt="MUTATIO618"
            className="footer__logo"
            onClick={() => {
              navigate(PAGE_PATHS.home);
            }}
          />
          <p className="footer__motto latin">Veritas in Mutatio</p>
          <a href="mailto:support@mutatio618.com" className="footer__email">
            support@mutatio618.com
          </a>
          <div className="footer__social">
            {SOCIAL_LINKS.map((social) => (
              <a
                key={social.name}
                href={social.url}
                className="footer__social-link"
                aria-label={social.name}
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d={social.icon} />
                </svg>
              </a>
            ))}
          </div>
        </div>
        <div>
          <h2 className="footer__column-title">Explore</h2>
          <ul className="footer__column-links">
            <li>
              <Link href={PAGE_PATHS['nostram-missionem']} className="footer__column-link">
                <span className="chi-rho-glyph">☧</span> Nostram Missionem
              </Link>
            </li>
            <li>
              <Link href={PAGE_PATHS.scientia} className="footer__column-link">
                <span className="sun-glyph">☉</span> Scientia
              </Link>
            </li>
            <li>
              <Link href={PAGE_PATHS['sovereign-stack']} className="footer__column-link">
                <span className="mercury-glyph">☿</span> STRUCTURA SUI IURIS
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h2 className="footer__column-title">The Store</h2>
          <ul className="footer__column-links">
            <li>
              <Link href={PAGE_PATHS['ohm-gear']} className="footer__column-link">
                <span className="ohm-glyph">Ω</span> ARMAMENTA
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h2 className="footer__column-title">Community</h2>
          <ul className="footer__column-links">
            <li>
              <Link href={PAGE_PATHS.contact} className="footer__column-link">
                Contact
              </Link>
            </li>
            <li>
              <Link href={PAGE_PATHS['sign-in']} className="footer__column-link">
                Sign In
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="footer__bottom">
        <p className="footer__copyright">© {new Date().getFullYear()} MUTATIO618. All rights reserved.</p>
        <nav className="footer__legal" aria-label="Legal">
          {LEGAL_LINKS.map((legal) => (
            <Link key={legal.href} href={legal.href} className="footer__legal-link">
              {legal.label}
            </Link>
          ))}
        </nav>
        {user !== null && (
          <Link href={PAGE_PATHS.arcana} className="footer__chi-rho" aria-label="Enter the inner sanctum">
            ☧
          </Link>
        )}
        <div className="footer__payments">
          {PAYMENT_MARKS.map((mark) => (
            <div key={mark.name} className="footer__payment-icon" title={mark.name}>
              <img src={mark.src} alt={mark.name} width={38} height={24} decoding="async" />
            </div>
          ))}
        </div>
      </div>
    </footer>
  );
}
